import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { X, Phone, MessageSquare, ExternalLink, UserPlus, Check, Plus, Loader2, Bot, Pause, Play, Calendar, Send } from "lucide-react";
import TagStrip from "@/components/TagStrip";

const BUCKET_META = {
  HOT:     { color: "bg-red-600 text-white",       chip: "🔥 HOT" },
  WARM:    { color: "bg-orange-500 text-white",    chip: "🟠 WARM" },
  NURTURE: { color: "bg-amber-400 text-slate-900", chip: "🟡 NURTURE" },
  LOW:     { color: "bg-slate-300 text-slate-800", chip: "⚪ LOW" },
};

function fmtDur(ms) {
  if (!ms) return "—";
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}
function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// Focused AI qualification view — does NOT replace the CRM lead drawer.
// Opens in-place from the AI Lead Manager queue so reps stay in context.
export default function AILeadDetailDrawer({ leadId, unknownInboundId, onClose }) {
  const nav = useNavigate();
  const [detail, setDetail] = useState(null);
  const [busy, setBusy]     = useState(false);
  const [markingReviewed, setMarking] = useState(false);
  // Team-member picker (replaces "Assign to Me")
  const [team, setTeam]           = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [assignFilter, setAssignFilter] = useState("");
  // ---- Add-to-CRM modal state (small dialog with prefilled seller info) ----
  // Fires only when the lead does NOT already have a CRM record
  // (board_id == null). Prefills seller/property/phone/AI score from the
  // AI Lead Manager detail payload; rep picks Pipeline, Stage, and
  // Assigned team member before saving.
  const [showAddCrm, setShowAddCrm]     = useState(false);
  const [boards, setBoards]             = useState([]);
  const [boardsBusy, setBoardsBusy]     = useState(false);
  const [crmBoardId, setCrmBoardId]     = useState("");
  const [crmColumnKey, setCrmColumnKey] = useState("");
  const [crmAssignee, setCrmAssignee]   = useState("");
  const [crmSaving, setCrmSaving]       = useState(false);
  // In-drawer human SMS reply (works whether or not AI is paused).
  const [smsReply, setSmsReply]   = useState("");
  const [smsSending, setSmsSending] = useState(false);

  useEffect(() => {
    if (!leadId) { setDetail(null); return; }
    let alive = true;
    setBusy(true);
    (async () => {
      try {
        const [{ data: d }, { data: t }] = await Promise.all([
          api.get(`/ai-lead-manager/lead/${leadId}`),
          api.get(`/team/members`).catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        setDetail(d);
        setTeam(Array.isArray(t) ? t : (t?.members || t?.users || []));
      } catch (e) {
        if (alive) toast.error(e?.response?.data?.detail || "Couldn't load lead");
      } finally { if (alive) setBusy(false); }
    })();
    return () => { alive = false; };
  }, [leadId]);

  if (!leadId && !unknownInboundId) return null;

  const lead    = detail?.lead;
  const call    = detail?.latest_call;
  const reasons = detail?.score_reasons || [];
  const sms     = detail?.sms_thread || [];
  const smsAi   = detail?.sms_ai || null;
  const prev    = detail?.previous_calls || [];
  const bucket  = lead?.bucket || "LOW";
  const meta    = BUCKET_META[bucket] || BUCKET_META.LOW;

  const markReviewed = async () => {
    if (!leadId) return;
    setMarking(true);
    try {
      await api.post(`/ai-lead-manager/lead/${leadId}/reviewed`);
      toast.success("Marked reviewed");
      onClose?.({ refetch: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setMarking(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="ai-lm-detail-drawer">
      {/* Backdrop */}
      <div className="flex-1 bg-slate-900/50 backdrop-blur-sm"
           onClick={() => onClose?.({})} />
      {/* Drawer */}
      <div className="w-full max-w-2xl bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className={`px-6 py-4 flex items-center justify-between ${meta.color}`}>
          <div>
            <div className="text-lg font-bold">{lead?.name || "Loading…"}</div>
            <div className="text-xs opacity-90">{lead?.property_address || (unknownInboundId ? "Unknown caller" : "")}</div>
          </div>
          <button onClick={() => onClose?.({})} className="p-1 rounded-full hover:bg-white/20"
                  data-testid="ai-lm-detail-close">
            <X size={18} />
          </button>
        </div>

        {/* Score + Bucket */}
        <div className="px-6 py-4 border-b flex items-center gap-4">
          <div className="text-4xl font-bold">{lead?.motivation_score ?? 0}<span className="text-lg text-muted-foreground">/100</span></div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${meta.color}`}>{meta.chip}</span>
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Recommended</div>
            <div className="text-sm font-bold text-slate-800">{lead?.next_action || "Review"}</div>
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {busy && <div className="text-center py-8 text-sm text-muted-foreground">Loading…</div>}

          {/* Tags — Phase 1 seller-tag chips + AI-suggested queue */}
          {leadId && !busy && (
            <section data-testid="ai-lm-tag-strip">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Tags</div>
              <TagStrip leadId={leadId} />
            </section>
          )}

          {/* Score reasons */}
          {reasons.length > 0 && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Score Reasons</div>
              <ul className="space-y-1 text-sm">
                {reasons.map((r, i) => <li key={i} className="flex gap-2"><span className="text-emerald-600">•</span> {r}</li>)}
              </ul>
            </section>
          )}

          {/* AI Summary */}
          {call?.summary && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">AI Summary</div>
              <div className="rounded-xl border-2 border-purple-200 bg-purple-50 p-3 text-sm whitespace-pre-wrap">{call.summary}</div>
            </section>
          )}

          {/* AI SMS Qualifier — live extracted signals + score from Claude.
              Only renders when there's been at least one AI SMS turn on
              this lead. Score reflects the LATEST turn (backend
              persists sms_ai_score on every reply, and motivation_score
              ratchets up so the lead climbs the queue in real time). */}
          {smsAi && (smsAi.score > 0 || (smsAi.extracted && Object.values(smsAi.extracted).some((v) => v != null))) && (
            <section data-testid="ai-lm-sms-ai-panel">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <Bot size={11} className="text-purple-600" /> AI SMS Qualifier
                </span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  smsAi.paused ? "bg-amber-100 text-amber-800"
                  : smsAi.handed_off ? "bg-slate-200 text-slate-700"
                  : smsAi.active ? "bg-emerald-100 text-emerald-800"
                  : "bg-slate-100 text-slate-600"
                }`}>
                  {smsAi.paused ? "Paused by rep"
                    : smsAi.handed_off ? `Closed · ${smsAi.last_action || "handed off"}`
                    : smsAi.active ? "AI is qualifying" : "Idle"}
                </span>
              </div>
              <div className="rounded-xl border-2 border-purple-200 bg-purple-50/60 p-3 space-y-2">
                <div className="flex items-center gap-3">
                  <div className="text-2xl font-bold text-purple-900">
                    {smsAi.score}<span className="text-xs text-purple-600">/100</span>
                  </div>
                  <div className="text-[11px] text-purple-800">
                    Qualification score from the latest AI SMS turn.
                    <br />
                    <span className="opacity-70">Hot leads bubble to the top automatically.</span>
                  </div>
                </div>
                {smsAi.extracted && (
                  <div className="grid grid-cols-2 gap-1.5 text-xs pt-1.5 border-t border-purple-200">
                    {smsAi.extracted.is_owner !== null && smsAi.extracted.is_owner !== undefined && (
                      <div><span className="text-muted-foreground">Owner:</span> <b>{smsAi.extracted.is_owner ? "Yes" : "No"}</b></div>
                    )}
                    {smsAi.extracted.motivation_reason && (
                      <div className="col-span-2"><span className="text-muted-foreground">Motivation:</span> <b>{smsAi.extracted.motivation_reason}</b></div>
                    )}
                    {smsAi.extracted.timeline_bucket && (
                      <div><span className="text-muted-foreground">Timeline:</span> <b>{String(smsAi.extracted.timeline_bucket).replace(/_/g, " ")}</b></div>
                    )}
                    {smsAi.extracted.condition && (
                      <div><span className="text-muted-foreground">Condition:</span> <b>{smsAi.extracted.condition}</b></div>
                    )}
                    {smsAi.extracted.asking_price != null && (
                      <div><span className="text-muted-foreground">Asking:</span> <b>${Number(smsAi.extracted.asking_price).toLocaleString()}</b></div>
                    )}
                    {smsAi.extracted.mortgage_balance != null && (
                      <div><span className="text-muted-foreground">Mortgage:</span> <b>${Number(smsAi.extracted.mortgage_balance).toLocaleString()}</b></div>
                    )}
                    {smsAi.extracted.other_offers !== null && smsAi.extracted.other_offers !== undefined && (
                      <div><span className="text-muted-foreground">Other offers:</span> <b>{smsAi.extracted.other_offers ? "Yes" : "No"}</b></div>
                    )}
                  </div>
                )}
                {/* Manual takeover / resume — only show while the AI hasn't
                    already closed the convo (fully_qualified / close_polite). */}
                {!smsAi.handed_off && (
                  <div className="pt-1.5 border-t border-purple-200">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="ai-lm-sms-takeover-btn"
                      className={`h-7 text-xs rounded-full ${
                        smsAi.paused ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                     : "border-amber-300 text-amber-700 hover:bg-amber-50"
                      }`}
                      onClick={async () => {
                        try {
                          const endpoint = smsAi.paused ? "resume" : "pause";
                          await api.post(`/ai-sms/${endpoint}/${leadId}`);
                          toast.success(smsAi.paused ? "AI resumed on this lead" : "AI paused — you're driving the thread now");
                          const { data } = await api.get(`/ai-lead-manager/lead/${leadId}`);
                          setDetail(data);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Failed to update AI state");
                        }
                      }}>
                      {smsAi.paused
                        ? (<><Play size={11} className="mr-1" /> Resume AI</>)
                        : (<><Pause size={11} className="mr-1" /> Take Over from AI</>)}
                    </Button>
                  </div>
                )}

                {/* Automated Cold Follow-Ups — 3/7/14-day AI drafts that
                    fire only if the seller stays silent. Any inbound
                    reply cancels the whole set. Rep can cancel individual
                    steps here if the seller went truly cold. */}
                {(smsAi.pending_followups || []).length > 0 && (
                  <div className="pt-1.5 border-t border-purple-200"
                       data-testid="ai-lm-followups-panel">
                    <div className="text-[10px] uppercase tracking-widest text-purple-700 mb-1 flex items-center gap-1">
                      <Calendar size={10} /> Scheduled AI Follow-Ups
                    </div>
                    <ul className="space-y-1">
                      {smsAi.pending_followups.map((f) => {
                        const runAt = f.run_at ? new Date(f.run_at) : null;
                        const days = Number(f.stage_days || 0);
                        return (
                          <li key={f.id}
                              data-testid={`ai-lm-followup-${f.stage_key}`}
                              className="flex items-center justify-between text-[11px] rounded-md bg-white/70 border border-purple-100 px-2 py-1">
                            <span className="text-purple-900">
                              <b>Day {days}</b>
                              <span className="text-purple-600 ml-1.5">
                                {runAt ? runAt.toLocaleString(undefined,
                                  { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                              </span>
                            </span>
                            <button
                              data-testid={`ai-lm-followup-cancel-${f.stage_key}`}
                              className="text-[10px] text-red-600 hover:text-red-800 hover:underline"
                              onClick={async () => {
                                try {
                                  await api.post(`/ai-sms/followup/${f.id}/cancel`);
                                  toast.success(`Day ${days} follow-up cancelled`);
                                  const { data } = await api.get(`/ai-lead-manager/lead/${leadId}`);
                                  setDetail(data);
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || "Cancel failed");
                                }
                              }}>Cancel</button>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="text-[10px] text-purple-600 mt-1 opacity-80">
                      Any inbound reply cancels the whole set automatically.
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* AI Qualification Answers */}
          {call && (call.timeline_bucket || call.occupancy || call.condition || call.asking_price || call.sentiment) && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">AI Qualification</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {call.timeline_bucket && <div><span className="text-muted-foreground">Timeline:</span> <b>{call.timeline_bucket}</b></div>}
                {call.occupancy       && <div><span className="text-muted-foreground">Occupancy:</span> <b>{call.occupancy}</b></div>}
                {call.condition       && <div><span className="text-muted-foreground">Condition:</span> <b>{call.condition}</b></div>}
                {call.asking_price    && <div><span className="text-muted-foreground">Asking:</span> <b>{call.asking_price}</b></div>}
                {call.sentiment       && <div><span className="text-muted-foreground">Sentiment:</span> <b>{call.sentiment}</b></div>}
                {call.seller_interested !== undefined && (
                  <div><span className="text-muted-foreground">Interested:</span> <b>{call.seller_interested ? "Yes" : "No"}</b></div>
                )}
              </div>
            </section>
          )}

          {/* Latest AI Voice Call */}
          {call && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                Latest AI Voice Call · {fmtDur(call.duration_ms)} · {fmtTime(call.created_at)}
              </div>
              {call.recording_url && (
                <audio controls className="w-full mb-2" data-testid="ai-lm-recording">
                  <source src={call.recording_url} />
                </audio>
              )}
              {call.transcript && (
                <details className="rounded-xl border p-3 text-xs">
                  <summary className="cursor-pointer font-semibold">Full transcript</summary>
                  <div className="mt-2 whitespace-pre-wrap text-slate-700">{call.transcript}</div>
                </details>
              )}
              {call.public_log_url && (
                <a href={call.public_log_url} target="_blank" rel="noreferrer"
                   className="text-xs text-purple-700 underline mt-2 inline-flex items-center gap-1">
                  Open in Retell <ExternalLink size={11} />
                </a>
              )}
            </section>
          )}

          {/* SMS Conversation */}
          {sms.length > 0 && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">SMS Conversation</div>
              <div className="space-y-2">
                {sms.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                      m.direction === "outbound"
                        ? (m.from_ai ? "bg-purple-100 text-purple-900" : "bg-emerald-100 text-emerald-900")
                        : "bg-slate-100 text-slate-900"
                    }`}>
                      <div className="whitespace-pre-wrap break-words">{m.body}</div>
                      <div className="text-[9px] opacity-60 mt-0.5">
                        {m.from_ai ? "AI · " : ""}{fmtTime(m.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* In-drawer human reply — send a text without leaving the queue.
              Pairs with the Take-Over button above: take over to silence the
              AI, then drive the thread right here. */}
          {lead?.phone && (
            <section data-testid="ai-lm-sms-composer">
              <div className="flex gap-2 items-end">
                <textarea
                  value={smsReply}
                  onChange={(e) => setSmsReply(e.target.value)}
                  rows={2}
                  placeholder={smsAi?.paused
                    ? "You're driving this thread — type a reply…"
                    : "Reply as human… (tip: Take Over above to pause the AI first)"}
                  className="flex-1 resize-none border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-300"
                  data-testid="ai-lm-sms-reply-input"
                />
                <Button
                  size="sm"
                  className="rounded-full"
                  disabled={!smsReply.trim() || smsSending}
                  data-testid="ai-lm-sms-send-btn"
                  onClick={async () => {
                    setSmsSending(true);
                    try {
                      await api.post("/twilio/sms/send", {
                        to: lead.phone, body: smsReply.trim(), lead_id: lead.id,
                      });
                      setSmsReply("");
                      const { data } = await api.get(`/ai-lead-manager/lead/${leadId}`);
                      setDetail(data);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Send failed");
                    } finally { setSmsSending(false); }
                  }}>
                  {smsSending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                </Button>
              </div>
            </section>
          )}

          {/* Previous AI interactions */}
          {prev.length > 0 && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Previous AI Interactions</div>
              <ul className="text-xs space-y-1.5">
                {prev.map((c) => (
                  <li key={c.call_id} className="flex justify-between gap-2 border-b pb-1.5">
                    <span className="truncate">{c.summary || c.outcome || "AI call"}</span>
                    <span className="text-muted-foreground shrink-0">{fmtTime(c.created_at)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Assigned rep */}
          <section className="text-xs">
            <span className="text-muted-foreground">Assigned rep: </span>
            <b>{lead?.assigned_rep?.name || lead?.assigned_rep?.email || "Unassigned"}</b>
          </section>
        </div>

        {/* Bottom action bar */}
        <div className="border-t px-6 py-3 flex flex-wrap gap-2 bg-slate-50 relative">
          <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="ai-lm-call-btn"
                  disabled={!lead?.phone}
                  onClick={() => {
                    // Stash a "pending call" hint so Dialer can pre-fill
                    // the customer card the moment it mounts (before the
                    // SIP softphone reaches Ready). Dialer already
                    // auto-dials when ?leadId= is present.
                    try {
                      localStorage.setItem("dialer.pendingCall", JSON.stringify({
                        lead_id:          lead.id,
                        phone:            lead.phone,
                        seller_name:      lead.name,
                        property_address: lead.property_address,
                        source:           "ai_lead_manager",
                        at:               Date.now(),
                      }));
                    } catch { /* ignore quota */ }
                    nav(`/dialer?leadId=${lead.id}`);
                    onClose?.({});
                  }}>
            <Phone size={13} className="mr-1.5" /> Call Seller
          </Button>
          <Button size="sm" variant="outline" className="rounded-full"
                  data-testid="ai-lm-text-btn"
                  disabled={!lead?.phone}
                  onClick={() => {
                    // SmsInbox reads ?lead=<id> and auto-opens the
                    // existing thread — or falls into compose with
                    // seller name/phone prefilled when no thread exists.
                    nav(`/sms-inbox?lead=${lead.id}&phone=${encodeURIComponent(lead.phone || "")}`);
                    onClose?.({});
                  }}>
            <MessageSquare size={13} className="mr-1.5" /> Text Seller
          </Button>
          {/* Adaptive CRM button.
              - lead.on_crm == true  → the seller is already on a pipeline
                board. Clicking opens that exact lead in /boards.
              - lead.on_crm == false → the seller was surfaced by AI
                without a CRM opportunity yet. Clicking opens the
                Add-to-CRM modal (prefilled seller info + Pipeline /
                Stage / Assignee pickers). Never creates duplicates —
                submit PATCHes the same lead row with board_id.  */}
          <Button size="sm" variant="outline" className="rounded-full"
                  data-testid="ai-lm-crm-btn"
                  onClick={async () => {
                    if (!leadId) return;
                    if (lead?.on_crm) {
                      nav(`/boards?open=${leadId}`);
                      onClose?.({});
                      return;
                    }
                    // Prefetch boards, pre-select the first pipeline +
                    // its first stage (typically "New Lead"), and pre-
                    // fill the assignee from whoever's currently on the
                    // AI lead (or leave blank).
                    setBoardsBusy(true);
                    try {
                      const { data } = await api.get("/boards");
                      const list = data?.boards || data || [];
                      setBoards(list);
                      const first = list[0];
                      setCrmBoardId(first?.id || "");
                      setCrmColumnKey((first?.columns || [])[0]?.key || "");
                      setCrmAssignee(lead?.assigned_rep?.id || "");
                      setShowAddCrm(true);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Couldn't load pipelines");
                    } finally { setBoardsBusy(false); }
                  }}>
            {boardsBusy && !lead?.on_crm
              ? (<><Loader2 size={13} className="mr-1.5 animate-spin" /> Loading…</>)
              : lead?.on_crm
                ? (<><ExternalLink size={13} className="mr-1.5" /> Open CRM</>)
                : (<><Plus size={13} className="mr-1.5" /> Add to CRM</>)}
          </Button>
          {/* Assign Team Member — replaces old "Assign to Me". */}
          <div className="relative">
            <Button size="sm" variant="outline" className="rounded-full"
                    data-testid="ai-lm-assign-btn"
                    onClick={() => setShowAssign((s) => !s)}>
              <UserPlus size={13} className="mr-1.5" />
              {lead?.assigned_rep ? `Assigned: ${lead.assigned_rep.name || lead.assigned_rep.email}` : "Assign Team Member"}
            </Button>
            {showAssign && (
              <div className="absolute bottom-full mb-2 left-0 w-72 rounded-xl border-2 shadow-2xl bg-white z-10 p-2"
                   data-testid="ai-lm-assign-popover">
                <Input placeholder="Search team member…"
                       value={assignFilter}
                       onChange={(e) => setAssignFilter(e.target.value)}
                       className="h-8 text-xs mb-2" />
                <div className="max-h-52 overflow-y-auto space-y-0.5">
                  {(team || [])
                    .filter((m) => !assignFilter.trim()
                      || (m.name || "").toLowerCase().includes(assignFilter.toLowerCase())
                      || (m.email || "").toLowerCase().includes(assignFilter.toLowerCase()))
                    .map((m) => (
                      <button key={m.id}
                        data-testid={`ai-lm-assign-opt-${m.id}`}
                        className={`w-full text-left px-2 py-1.5 rounded text-xs hover:bg-slate-100 flex items-center justify-between ${
                          lead?.assigned_rep?.id === m.id ? "bg-emerald-50 font-semibold" : ""
                        }`}
                        onClick={async () => {
                          try {
                            await api.post(`/ai-lead-manager/lead/${leadId}/assign`, { assigned_to: m.id });
                            toast.success(`Assigned to ${m.name || m.email}`);
                            setShowAssign(false);
                            // Refresh lead detail so the button label updates.
                            const { data } = await api.get(`/ai-lead-manager/lead/${leadId}`);
                            setDetail(data);
                          } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
                        }}>
                        <span>{m.name || m.email}</span>
                        {lead?.assigned_rep?.id === m.id && <Check size={12} className="text-emerald-600" />}
                      </button>
                    ))}
                  {lead?.assigned_rep && (
                    <button data-testid="ai-lm-assign-clear"
                      className="w-full text-left px-2 py-1.5 rounded text-xs hover:bg-red-50 text-red-700 border-t mt-1 pt-2"
                      onClick={async () => {
                        try {
                          await api.post(`/ai-lead-manager/lead/${leadId}/assign`, { assigned_to: null });
                          toast.success("Unassigned");
                          setShowAssign(false);
                          const { data } = await api.get(`/ai-lead-manager/lead/${leadId}`);
                          setDetail(data);
                        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                      }}>Unassign</button>
                  )}
                </div>
              </div>
            )}
          </div>
          <Button size="sm" className="rounded-full ml-auto bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="ai-lm-reviewed-btn"
                  disabled={markingReviewed || !leadId}
                  onClick={markReviewed}>
            <Check size={13} className="mr-1.5" /> Mark Reviewed
          </Button>
        </div>
      </div>

      {/* ================================================================
          Add-to-CRM modal — small dialog with seller info prefilled.
          Rep picks Pipeline + Stage + Assignee, then clicks
          "Add Seller to CRM". This PATCHes the same db.leads row with
          board_id + column_key (never creates duplicates), then re-fetches
          the AI Lead Manager detail so the button flips to "Open CRM"
          and preserves all AI qualification data / SMS / call history.
          ================================================================ */}
      <Dialog open={showAddCrm} onOpenChange={(v) => !crmSaving && setShowAddCrm(v)}>
        <DialogContent className="max-w-md" data-testid="ai-lm-add-crm-modal"
                       onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Add Seller to CRM Pipeline</DialogTitle>
            <DialogDescription>
              Move this AI-qualified seller into your CRM. Qualification
              data, AI score, SMS history, and call summaries are preserved.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-2">
            {/* Read-only seller summary */}
            <div className="rounded-xl border bg-slate-50 p-3 text-xs space-y-1">
              <div><span className="text-muted-foreground">Seller:</span> <b>{lead?.name || "—"}</b></div>
              <div><span className="text-muted-foreground">Property:</span> <b>{lead?.property_address || "—"}</b></div>
              <div><span className="text-muted-foreground">Phone:</span> <b>{lead?.phone || "—"}</b></div>
              <div className="flex gap-4">
                <span><span className="text-muted-foreground">AI Score:</span> <b>{lead?.motivation_score ?? 0}/100</b></span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${meta.color}`}>{meta.chip}</span>
              </div>
              {call?.summary && (
                <div className="text-[11px] text-slate-600 pt-1 border-t mt-2 line-clamp-2">
                  <span className="text-muted-foreground">AI Summary:</span> {call.summary}
                </div>
              )}
            </div>

            {/* Pipeline picker */}
            <div>
              <Label className="text-xs">Pipeline</Label>
              <select
                data-testid="ai-lm-add-crm-pipeline"
                value={crmBoardId}
                onChange={(e) => {
                  const bid = e.target.value;
                  setCrmBoardId(bid);
                  const b = boards.find((x) => x.id === bid);
                  setCrmColumnKey((b?.columns || [])[0]?.key || "");
                }}
                className="w-full h-9 border rounded-md px-2 text-sm mt-1 bg-white">
                {boards.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
                {boards.length === 0 && <option value="">No pipelines available</option>}
              </select>
            </div>

            {/* Stage picker (defaults to first column — typically "New Lead") */}
            <div>
              <Label className="text-xs">Stage</Label>
              <select
                data-testid="ai-lm-add-crm-stage"
                value={crmColumnKey}
                onChange={(e) => setCrmColumnKey(e.target.value)}
                className="w-full h-9 border rounded-md px-2 text-sm mt-1 bg-white">
                {(boards.find((b) => b.id === crmBoardId)?.columns || []).map((c) => (
                  <option key={c.key} value={c.key}>{c.label || c.name || c.key}</option>
                ))}
              </select>
            </div>

            {/* Assigned team member (optional) */}
            <div>
              <Label className="text-xs">Assigned Team Member</Label>
              <select
                data-testid="ai-lm-add-crm-assignee"
                value={crmAssignee}
                onChange={(e) => setCrmAssignee(e.target.value)}
                className="w-full h-9 border rounded-md px-2 text-sm mt-1 bg-white">
                <option value="">— Unassigned —</option>
                {(team || []).map((m) => (
                  <option key={m.id} value={m.id}>{m.name || m.email}</option>
                ))}
              </select>
            </div>
          </div>

          <DialogFooter className="pt-3">
            <Button variant="outline" onClick={() => setShowAddCrm(false)} disabled={crmSaving}
                    data-testid="ai-lm-add-crm-cancel">Cancel</Button>
            <Button
              data-testid="ai-lm-add-crm-submit"
              disabled={crmSaving || !crmBoardId || !crmColumnKey}
              onClick={async () => {
                if (!leadId) return;
                setCrmSaving(true);
                try {
                  // Preserve AI qualification: we only add board_id +
                  // column_key to the existing lead row. All Retell
                  // calls, SMS threads, motivation_score, and reasons
                  // remain attached to the same lead id.
                  await api.patch(`/leads/${leadId}`, {
                    board_id:   crmBoardId,
                    column_key: crmColumnKey,
                  });
                  // Assignment (optional) uses the AI Lead Manager assign
                  // endpoint so the assigned_rep field updates on the
                  // detail payload immediately.
                  if (crmAssignee !== (lead?.assigned_rep?.id || "")) {
                    try {
                      await api.post(`/ai-lead-manager/lead/${leadId}/assign`,
                        { assigned_to: crmAssignee || null });
                    } catch (e) { /* assignment failure is non-fatal */ }
                  }
                  const boardName = boards.find((b) => b.id === crmBoardId)?.name || "CRM";
                  toast.success(`Added to CRM — ${boardName}`);
                  // Re-fetch so the button flips to "Open CRM" and the
                  // "Assigned:" label refreshes without a page reload.
                  const { data } = await api.get(`/ai-lead-manager/lead/${leadId}`);
                  setDetail(data);
                  setShowAddCrm(false);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Add to CRM failed");
                } finally { setCrmSaving(false); }
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {crmSaving ? (<><Loader2 size={13} className="mr-1.5 animate-spin" /> Saving…</>)
                         : (<><Plus size={13} className="mr-1.5" /> Add Seller to CRM</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
